import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Plus, Search, CalendarClock, PlayCircle, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { DialogTrigger } from '../components/ui/dialog';

import CourseTable from './courses/CourseTable';
import CourseFormDialog from './courses/CourseFormDialog';
import { useCourseForm } from './courses/useCourseForm';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Courses = () => {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [activeTab, setActiveTab] = useState('en_curso');
  
  // Estado del catálogo
  const [families, setFamilies] = useState({});
  const [templates, setTemplates] = useState([]);
  const [directors, setDirectors] = useState([]);
  const [onlineCourses, setOnlineCourses] = useState([]);
  const [instructors, setInstructors] = useState([]);

  // Hook del formulario
  const {
    formData,
    setFormData,
    editingCourse,
    handleNumeroSesionesChange,
    updateSesion,
    toggleReconocimiento,
    resetForm,
    loadCourseData,
    handleTemplateSelect,
    prepareDataForSubmit
  } = useCourseForm();

  const canEdit = user?.role === 'Administrador' || user?.role === 'Formador';

  useEffect(() => {
    fetchCourses();
    fetchFamilies();
    fetchTemplates();
    fetchDirectors();
    fetchOnlineCourses();
    fetchInstructors();
  }, []);

  const fetchFamilies = async () => {
    try {
      const response = await axios.get(`${API_URL}/courses/families`);
      setFamilies(response.data);
    } catch (error) {
      console.error('Error fetching families:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API_URL}/courses/templates`);
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const fetchDirectors = async () => {
    try {
      const response = await axios.get(`${API_URL}/users/directors`);
      setDirectors(response.data);
    } catch (error) {
      console.error('Error fetching directors:', error);
    }
  };

  const fetchInstructors = async () => {
    try {
      const response = await axios.get(`${API_URL}/users/instructors`);
      setInstructors(response.data);
    } catch (error) {
      console.error('Error fetching instructors:', error);
    }
  };

  const fetchOnlineCourses = async () => {
    try {
      const response = await axios.get(`${API_URL}/online-courses`);
      setOnlineCourses(response.data.filter(c => c.is_published));
    } catch (error) {
      console.error('Error fetching online courses:', error);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API_URL}/courses`);
      setCourses(response.data);
    } catch (error) {
      toast.error('Error al cargar los cursos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nombre || !formData.horas) {
      toast.error('Por favor, completa los campos obligatorios: Nombre y Horas');
      return;
    }
    
    const dataToSend = prepareDataForSubmit();
    
    try {
      if (editingCourse) {
        await axios.put(`${API_URL}/courses/${editingCourse.id}`, dataToSend);
        toast.success('Curso actualizado correctamente');
      } else {
        await axios.post(`${API_URL}/courses`, dataToSend);
        toast.success('Curso creado correctamente');
      }
      setShowDialog(false);
      resetForm();
      fetchCourses();
    } catch (error) {
      console.error('Error al guardar curso:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar el curso');
    }
  };

  const handleDelete = async (courseId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este curso?')) return;
    
    try {
      await axios.delete(`${API_URL}/courses/${courseId}`);
      toast.success('Curso eliminado correctamente');
      fetchCourses();
    } catch (error) {
      toast.error('Error al eliminar el curso');
    }
  };

  const handleDuplicate = async (courseId) => {
    try {
      await axios.post(`${API_URL}/courses/${courseId}/duplicate`);
      toast.success('Curso duplicado correctamente');
      fetchCourses();
    } catch (error) {
      toast.error('Error al duplicar el curso');
    }
  };

  const handleDownloadInfoPDF = async (course) => {
    try {
      const response = await axios.get(`${API_URL}/courses/${course.id}/info/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const nombreArchivo = course.nombre.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 30);
      link.setAttribute('download', `info_${nombreArchivo}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF descargado correctamente');
    } catch (error) {
      toast.error('Error al descargar el PDF');
      console.error('Error downloading PDF:', error);
    }
  };

  const openEditDialog = (course) => {
    loadCourseData(course);
    setShowDialog(true);
  };

  const openCreateDialog = () => {
    resetForm();
    setShowDialog(true);
  };

  const onTemplateSelect = (templateId) => {
    handleTemplateSelect(templateId, templates);
  };

  const filteredCourses = courses.filter(course =>
    course.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (course.descripcion && course.descripcion.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Clasificar cursos en pestañas
  const { programadas, enCurso, finalizadas } = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const result = { programadas: [], enCurso: [], finalizadas: [] };

    filteredCourses.forEach(course => {
      if (course.estado_curso === 'cerrado') {
        result.finalizadas.push(course);
      } else {
        const fechaInicio = course.fecha_inicio ? new Date(course.fecha_inicio) : null;
        if (fechaInicio && fechaInicio > today) {
          result.programadas.push(course);
        } else {
          result.enCurso.push(course);
        }
      }
    });

    return result;
  }, [filteredCourses]);

  const tabs = [
    { id: 'en_curso', label: 'En Curso', icon: PlayCircle, count: enCurso.length, color: 'text-blue-600 border-blue-600 bg-blue-50' },
    { id: 'programadas', label: 'Programadas', icon: CalendarClock, count: programadas.length, color: 'text-amber-600 border-amber-600 bg-amber-50' },
    { id: 'finalizadas', label: 'Finalizadas', icon: CheckCircle2, count: finalizadas.length, color: 'text-green-600 border-green-600 bg-green-50' },
  ];

  const currentCourses = activeTab === 'en_curso' ? enCurso : activeTab === 'programadas' ? programadas : finalizadas;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#004E89]"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="courses-container">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-manrope font-bold text-4xl text-slate-900" data-testid="courses-title">
              Cursos
            </h1>
            <p className="font-inter text-slate-600 mt-2">
              Gestión de Formación
            </p>
          </div>
          {canEdit && (
            <Button
              onClick={openCreateDialog}
              className="bg-[#004E89] hover:bg-[#003B66]"
              data-testid="courses-create-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Crear Curso
            </Button>
          )}
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
          <Input
            type="text"
            placeholder="Buscar cursos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="courses-search-input"
          />
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-slate-200" data-testid="courses-tabs">
          {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                  isActive 
                    ? tab.color + ' border-current' 
                    : 'text-slate-500 border-transparent hover:text-slate-700 hover:border-slate-300'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
                <span className={`ml-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
                  isActive ? 'bg-white/80 text-inherit' : 'bg-slate-100 text-slate-500'
                }`}>
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Courses Table */}
        <CourseTable
          courses={currentCourses}
          canEdit={canEdit}
          onEdit={openEditDialog}
          onDelete={handleDelete}
          onDuplicate={handleDuplicate}
          onDownloadPDF={handleDownloadInfoPDF}
        />

        {/* Course Form Dialog */}
        <CourseFormDialog
          open={showDialog}
          onOpenChange={setShowDialog}
          editingCourse={editingCourse}
          formData={formData}
          setFormData={setFormData}
          onSubmit={handleSubmit}
          onTemplateSelect={onTemplateSelect}
          handleNumeroSesionesChange={handleNumeroSesionesChange}
          updateSesion={updateSesion}
          toggleReconocimiento={toggleReconocimiento}
          families={families}
          templates={templates}
          directors={directors}
          instructors={instructors}
          onlineCourses={onlineCourses}
        />
      </div>
    </Layout>
  );
};

export default Courses;
